import { useEffect, useState } from "react";
import { fetchWaxPrice, type WaxPrice } from "../api/waxPrice";

const REFRESH_MS = 60000; // CoinGecko's free tier is rate-limited; the 24h change doesn't move fast enough to need more

/**
 * WAX/USD price box, styled after the colored index boxes on finance
 * sites like CNBC — solid green when WAX is up over the last 24h, solid
 * red when it's down, with the price, the change, and a last-updated
 * time. Rendered on the homepage (Marketplace.tsx) next to the neon
 * welcome sign, in one row — this component only renders the colored box
 * itself, so the parent controls the surrounding layout.
 */
export function WaxPriceBox() {
  const [price, setPrice] = useState<WaxPrice | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;

    function load() {
      fetchWaxPrice()
        .then((p) => {
          if (cancelled) return;
          setPrice(p);
          setUpdatedAt(new Date());
        })
        .catch(() => {
          /* best-effort — keep showing the last good price if this fetch fails */
        });
    }

    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!price) return null;

  const isUp = price.change24h >= 0;

  return (
    <div className={"wax-price-box" + (isUp ? " wax-price-box--up" : " wax-price-box--down")}>
      <span className="wax-price-box__symbol">WAXP</span>
      <span className="wax-price-box__price">
        $
        {price.usd.toLocaleString(undefined, {
          minimumFractionDigits: 4,
          maximumFractionDigits: 6,
        })}
      </span>
      <span className="wax-price-box__change">
        {isUp ? "▲" : "▼"} {Math.abs(price.change24h).toFixed(2)}%
      </span>
      {updatedAt && <span className="wax-price-box__meta">Last | {updatedAt.toLocaleTimeString()}</span>}
    </div>
  );
}
